/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { ClientUserProvider, ReportLinksProvider, ReportProvider } from '../../../../../providers';
import { ClientUser, Helpers, Report, ReportEngageResult, User } from '../../../../../model';
import { ReportService } from '../../../../../services/report.service';
import { StageService, UserService, ModeService } from '../../../../../services';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import * as moment from 'moment';
import { Router } from '@angular/router';
import { SystemType } from 'src/app/model/enums/system.type';
import Quill from 'quill';
import BlotFormatter from 'quill-blot-formatter';
import { PresetValue } from 'src/app/model/preset-value';
import { SearchableItem } from 'src/app/model/interface';
import { PreviewSize } from 'src/app/model/enums/preview.size';
import { ConfirmationDialogComponent } from 'src/app/components/fields';
import { LogService } from 'src/app/services';
Quill.register('modules/blotFormatter', BlotFormatter);

@Component({
  selector: 'engage-report-file-modal',
  templateUrl: './engage-report-file.modal.html',
  styleUrls: ['./engage-report-file.modal.scss']
})
export class EngageReportFileModal implements OnInit, OnDestroy {
  subject: Subject<boolean>;
  subscription: any;

  currentReport: Report;
  private reportSub: any;

  isLoading = false;
  isLoadingPreview = false;

  userTypes: PresetValue[] = [];
  cc: string[] = [];
  bcc: string[] = [];

  PreviewSize = PreviewSize;
  size: PreviewSize = PreviewSize.MEDIUM;
  confirmRef: BsModalRef;
  editorStyle = { 'height': '400px' };

  quillModules = {
    blotFormatter: {},
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link', 'image']
    ]
  };
  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '25rem',
    minHeight: '5rem',
    placeholder: 'Enter text here...',
    translate: 'no'
  };

  htmlContent = '';
  htmlSubject = 'Entity Name (DBA Name)';
  htmlHead = '';
  htmlBody = '';
  user: User;
  currentMode: SystemType;
  modeSub: any;
  disclaimer: string = `<div dir="ltr" style="text-size-adjust: auto;">
                <span style="caret-color: rgb(0, 0, 0); color: rgb(0, 0, 0); font-family: arial, helvetica, sans-serif; font-size: 12.8px; text-size-adjust: auto;">
                <span style="text-size-adjust: auto; color: rgb(102, 102, 102); font-family: &quot;courier new&quot;, monospace; font-size: x-small;">
                The information transmitted herewith is sensitive information intended only for use to the individual or entity to which it is addressed. &nbsp;</span>
                <span style="text-size-adjust: auto; color: rgb(102, 102, 102); font-family: &quot;courier new&quot;, monospace; font-size: x-small;">
                If the reader of this message is not the intended recipient, you are hereby notified that any review, retransmission, dissemination, distribution,
                copying or other use of, or taking of any action in reliance upon, this information is strictly prohibited.&nbsp; If you have received this communication in error,
                please contact the sender and delete the material from your computer.</span></span>
            </div>`;

  constructor(
    private modalService: BsModalService,
    private reportProvider: ReportProvider,
    private reportService: ReportService,
    private stageService: StageService,
    private userService: UserService,
    private reportLinkProvider: ReportLinksProvider,
    private router: Router,
    private modeService: ModeService,
    public userProvider: ClientUserProvider,
    private logService: LogService
  ) { }

  ngOnInit(): void {
    // Fix for when user clicks `ESC` instead of clicking, no Event was firing
    this.subscription = this.modalService.onHide.subscribe((reason: string) => {
      if (reason === 'esc') {
        if (!this.isLoading)
          this.onCancel();
      }
    });

    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      if (currentReport) {
        this.currentReport = currentReport;
        if (this.currentReport != null)
          if (this.currentReport.ID) {
            this.user = this.userService.getCurrentUser();
            this.constructHtml();
          }
      }
    });

    this.modeSub = this.modeService.mode$.subscribe(mode => this.currentMode = mode);
  }

  ngOnDestroy(): void {
    if (this.subscription)
      this.subscription.unsubscribe();

    if (this.reportSub)
      this.reportSub.unsubscribe();

    if (this.modeSub)
      this.modeSub.unsubscribe();
  }

  onConfirm(): void {
    this.isLoading = true;

    let defaultSig = `<div style="text-align:left;font-weight:bold;" dir="ltr">${this.user.getName()}</div>`;
    defaultSig += '<div style="text-align:left;margin-bottom:3px;" dir="ltr">Reliant Business Valuation</div>';
    defaultSig += '<img src="https://www.reliantvalue.com/wp-content/uploads/2017/07/Updated-small.jpg" width="130px"/><br/>';
    defaultSig += '<p style="text-align:left;font-family:Helvetica,Arial,sans-serif;font-size:12px;margin-bottom:0;margin-top:3px;color:#5F5F5F;line-height:135%;">';
    defaultSig += 'Your Most Reputable Source for SBA Values&reg; <br/>';
    defaultSig += '1246 S. River Road, Ste 101 | Cranbury, NJ 08512 <br/>';
    defaultSig += ' Office: (908) 888-6030  | Fax: (908) 888-6040 <br/>';
    defaultSig += '<a href="www.reliantvalue.com">www.reliantvalue.com</a></p>';
    defaultSig += '<br/>';

    let signature = this.user.SIGNATURE ? this.user.SIGNATURE + '<br/>' : defaultSig;
    let content = this.htmlContent + signature + this.disclaimer;

    this.reportProvider.engageReportFile(this.currentReport.ID, this.htmlSubject, content, this.currentMode, this.cc, this.bcc)
      .subscribe(data => {
        this.isLoading = false;
        if (data.success) {
          const engageResult = ReportEngageResult.parse(data.result);

          this.reportService.addStageHistory(engageResult.HISTORY_STAGE);
          this.currentReport.STAGE = this.stageService.getStageById(engageResult.HISTORY_STAGE.SID);
          this.currentReport.ASSIGNEE = engageResult.HISTORY_STAGE.ASSIGNEE;
          this.currentReport.STATE = engageResult.HISTORY_STAGE.STATE;
          this.currentReport.DATE_ENGAGED = moment(engageResult.DATE_ENGAGED).format('MMM DD, YYYY');
          this.currentReport.DATE_DUE = moment(engageResult.DATE_DUE).format('MMM DD, YYYY');

          const config = Helpers.getModalConfig();
          config.class = 'modal-dialog-centered';

          this.confirmRef = this.modalService.show(ConfirmationDialogComponent, config);
          const ackSubject = new Subject<any>();
          this.confirmRef.content.subject = ackSubject;
          this.confirmRef.content.title = 'Success';
          this.confirmRef.content.text = 'Your email has been successfully sent!';
          this.confirmRef.content.actionButton = 'Ok';
          this.confirmRef.content.showCancel = false;

          ackSubject.subscribe(value => {
            if (value) {
              this.subject.next(true);
              this.subject.complete();
            }
          });

        } else this.logService.error(JSON.stringify(data, null, 2));
      }, error => {
        this.isLoading = false;
        this.logService.error(error);
      });
  }

  onCancel(): void {
    if (!this.isLoading) {
      this.subject.next(false);
      this.subject.complete();
    }
  }

  getSigner(): ClientUser {
    if (this.currentReport.CLIENT_USERS) {
      for (const user of this.currentReport.CLIENT_USERS) {
        if (user.EMAIL_ENGAGE)
          return user.CLIENT_USER;
      }
    }

    return null;
  }

  private constructHtml(): void {

    this.htmlHead = `<div id="userName">${this.getSigner()?.NAME ? Helpers.getFirstName(this.getSigner()?.NAME) : ""},</div>`;

    let linkId = '';
    if (this.currentReport.LINKS && this.currentReport.LINKS.length > 0)
      linkId = this.currentReport.LINKS[0].HASH;

    let dateRange;
    let currentDate = moment(new Date()).format("MM/DD/YYYY");

    let startDate, endDate;

    if (this.currentReport.DATE_ANTICIPATED) {
        startDate = moment(this.currentReport.DATE_ANTICIPATED);
        endDate = moment(Helpers.businessAdd(this.currentReport.DATE_ANTICIPATED, this.currentReport.turnDiff));
    } else {
        startDate = moment(Helpers.businessAdd(currentDate, this.currentReport.TURN_FROM));
        endDate = moment(Helpers.businessAdd(currentDate, this.currentReport.TURN_TO));
    }

    if (this.currentReport.turnDiff == 0) {
        dateRange = `<b>${startDate.format("MMMM Do")}</b>`;
    } else if (startDate.month() === endDate.month()) {
        dateRange = `<b>${startDate.format("MMMM Do")} and ${endDate.format("Do")}</b>`;
    } else {
        dateRange = `<b>${startDate.format("MMMM Do")} and ${endDate.format("MMMM Do")}</b>`;
    }

    let buff = `<div>The engagement letter for this order has been received and we will begin working on this business valuation as of today.
      The estimated completion date of the report is between ${dateRange} barring any unforeseen complexities and assuming a timely contact response.
                </div>
                <div><b>Report Tracker -</b> Please click the following link to track the status of this report at any time: <a href="${this.hostName}/link/${linkId}" target="_blank">${this.hostName}/link/${linkId}</a>.
                </div><br/>`


    this.htmlBody = buff;
    this.htmlSubject = `${this.currentReport.getEntitiesName()} - Report Engaged`;
    this.htmlContent = (this.htmlHead += this.htmlBody);
  }

  gotoProfile(): void {
    this.router.navigate(['/account']);
  }

  get hostName(): string {
    return window.location.protocol + '//' + window.location.host.replace('4200', '5000');
  }

  ccChanged(recs: SearchableItem[]): void {
    this.cc = [];
    if (recs != null) {
      this.cc = recs
        .map(r => (<ClientUser>r.getValue()).EMAIL);
    }
  }

  bccChanged(recs: SearchableItem[]): void {
    this.bcc = [];
    if (recs != null) {
      this.bcc = recs
        .map(r => (<ClientUser>r.getValue()).EMAIL);
    }
  }

  expandArea(): void {
    if (this.size === PreviewSize.MEDIUM) {
      this.size = PreviewSize.LARGE;
      this.editorStyle = { 'height': 'auto' };
    } else {
      this.size = PreviewSize.MEDIUM;
      this.editorStyle = { 'height': '400px' };
    }
  }

}
